import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Stack,
  Grid,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  FormControlLabel,
  Switch
} from '@mui/material';
import { Save, ArrowLeft } from 'lucide-react';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import EquipmentService, { EquipmentFormData } from '../../services/equipmentService';
import { EquipmentType, EquipmentBrand } from '../../types/equipment';
import ConfirmationDialog from '../../components/common/ConfirmationDialog';
import SuccessSnackbar from '../../components/common/SuccessSnackbar';

// Remover arrays de mock não utilizados
// const equipmentTypes: EquipmentType[] = [...]; 
// const equipmentBrands: EquipmentBrand[] = [...];

const EquipmentFormPage = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const equipmentService = useMemo(() => new EquipmentService(), []);
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initialLoading, setInitialLoading] = useState(false);
  const [typeOptions, setTypeOptions] = useState<EquipmentType[]>([]);
  const [brandOptions, setBrandOptions] = useState<EquipmentBrand[]>([]);
  const [optionsError, setOptionsError] = useState<string | null>(null);
  // Estados para confirmação e snackbar
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const { 
    control, 
    handleSubmit, 
    reset, 
    formState: { errors, isSubmitting } 
  } = useForm<EquipmentFormData>({ 
    defaultValues: {
      equipment_type_id: undefined,
      equipment_brand_id: undefined,
      model: '',
      serial_number: '',
      purchase_date: null,
      discharge_date: null,
      discharge_reason: '',
      observation: ''
    }
  });

  // Buscar Tipos e Marcas ao montar
  useEffect(() => {
    setOptionsError(null);
    Promise.all([
      equipmentService.getEquipmentTypes(),
      equipmentService.getEquipmentBrands()
    ]).then(([types, brands]) => {
      setTypeOptions(types);
      setBrandOptions(brands);
    }).catch(err => {
      console.error("Erro ao buscar opções:", err);
      setOptionsError('Falha ao carregar opções de Tipo/Marca.');
    });
  }, [equipmentService]);

  // Buscar dados do equipamento para edição
  useEffect(() => {
    if (isEditMode && id) {
      setInitialLoading(true);
      equipmentService.getById(parseInt(id, 10))
        .then(equipment => {
          const formattedData = {
            ...equipment,
            purchase_date: equipment.purchase_date ? new Date(equipment.purchase_date).toISOString().split('T')[0] : null,
            discharge_date: equipment.discharge_date ? new Date(equipment.discharge_date).toISOString().split('T')[0] : null,
            // Garantir que IDs sejam undefined se não vierem da API ou forem null
            equipment_type_id: equipment.equipment_type_id ?? undefined,
            equipment_brand_id: equipment.equipment_brand_id ?? undefined,
          };
          reset(formattedData as EquipmentFormData);
          setError(null);
        })
        .catch(err => {
          console.error("Erro ao buscar equipamento para edição:", err);
          setError(err.message || 'Falha ao carregar dados do equipamento.');
        })
        .finally(() => {
          setInitialLoading(false);
        });
    }
  }, [id, isEditMode, reset, equipmentService]);

  // Função que REALMENTE salva
  const handleConfirmSave: SubmitHandler<EquipmentFormData> = async (data) => {
    setIsConfirmDialogOpen(false); // Fecha diálogo
    setLoading(true);
    setError(null);

    const typeId = data.equipment_type_id ? parseInt(String(data.equipment_type_id), 10) : NaN;
    const brandId = data.equipment_brand_id ? parseInt(String(data.equipment_brand_id), 10) : NaN;

    if (isNaN(typeId) || isNaN(brandId)){
        setError("Tipo e Marca são obrigatórios.");
        setLoading(false);
        return;
    }

    const dataToSubmit = {
      ...data,
      equipment_type_id: typeId,
      equipment_brand_id: brandId,
    };

    try {
      let successMessage = '';
      if (isEditMode && id) {
        await equipmentService.update(parseInt(id, 10), dataToSubmit);
        successMessage = 'Equipamento atualizado com sucesso.';
      } else {
        await equipmentService.create(dataToSubmit);
        successMessage = 'Equipamento criado com sucesso.';
      }

      // Mostrar Snackbar e agendar navegação
      setSnackbarMessage(successMessage);
      setSnackbarOpen(true);
      setTimeout(() => {
        navigate('/equipment');
      }, 1500);

    } catch (err: any) {
      console.error("Erro ao salvar equipamento:", err);
      setError(err.message || 'Erro ao salvar equipamento.');
    } finally {
      setLoading(false);
    }
  };

  // Função chamada pelo botão SALVAR (agora só abre o diálogo)
  const onSubmit = () => {
    // A validação é feita pelo react-hook-form
    setIsConfirmDialogOpen(true);
  };

  // Função para fechar diálogo sem salvar
  const handleCancelSave = () => {
    setIsConfirmDialogOpen(false);
  };

  // Função para fechar o Snackbar
  const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleCancel = () => {
    navigate('/equipment');
  };

  if (initialLoading && isEditMode) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Typography variant="h5" component="h1" sx={{ mb: 2 }}>
        {isEditMode ? 'Editar Equipamento' : 'Novo Equipamento'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5, py: 0.5, fontSize: '0.875rem' }}>
          {error}
        </Alert>
      )}
      {optionsError && (
        <Alert severity="warning" sx={{ mb: 1.5, py: 0.5, fontSize: '0.875rem' }}>
          {optionsError}
        </Alert>
      )}

      <Card elevation={2}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={1}>
              {/* Linha 1: Tipo, Marca, Modelo, Nº Série */}
              <Grid item xs={12} md={3}>
                <Controller
                  name="equipment_type_id"
                  control={control}
                  rules={{ required: 'Tipo é obrigatório' }} 
                  render={({ field }) => (
                    <FormControl fullWidth size="small" error={!!errors.equipment_type_id} disabled={typeOptions.length === 0}>
                      <InputLabel size="small" sx={{ fontSize: '0.875rem' }}>Tipo *</InputLabel>
                      <Select 
                        {...field} 
                        value={field.value ?? ''} // Tratar null para o Select
                        label="Tipo *" 
                        size="small"
                        sx={{ height: 40, fontSize: '0.875rem' }}
                      >
                        {/* O value="" corresponde ao field.value === null tratado acima */}
                        <MenuItem value="" sx={{ fontSize: '0.875rem' }}><em>Selecione...</em></MenuItem>
                        {typeOptions.map(opt => <MenuItem key={opt.id} value={opt.id} sx={{ fontSize: '0.875rem' }}>{opt.name}</MenuItem>)}
                      </Select>
                      {errors.equipment_type_id && <Typography color="error" variant="caption">{errors.equipment_type_id.message}</Typography>}
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Controller
                  name="equipment_brand_id"
                  control={control}
                  rules={{ required: 'Marca é obrigatória' }}
                  render={({ field }) => (
                    <FormControl fullWidth size="small" error={!!errors.equipment_brand_id} disabled={brandOptions.length === 0}>
                      <InputLabel size="small" sx={{ fontSize: '0.875rem' }}>Marca *</InputLabel>
                      <Select 
                        {...field} 
                        value={field.value ?? ''} // Tratar null para o Select
                        label="Marca *" 
                        size="small"
                        sx={{ height: 40, fontSize: '0.875rem' }}
                      >
                         {/* O value="" corresponde ao field.value === null tratado acima */}
                        <MenuItem value="" sx={{ fontSize: '0.875rem' }}><em>Selecione...</em></MenuItem>
                        {brandOptions.map(opt => <MenuItem key={opt.id} value={opt.id} sx={{ fontSize: '0.875rem' }}>{opt.name}</MenuItem>)}
                      </Select>
                      {errors.equipment_brand_id && <Typography color="error" variant="caption">{errors.equipment_brand_id.message}</Typography>}
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Controller
                  name="model"
                  control={control}
                  render={({ field }) => (
                    <TextField 
                      {...field} 
                      label="Modelo" 
                      fullWidth 
                      size="small" 
                      disabled={loading || isSubmitting} 
                      InputLabelProps={{ sx: { fontSize: '0.875rem' } }} 
                      InputProps={{ sx: { height: 40, fontSize: '0.875rem' } }}
                      value={field.value || ''}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Controller
                  name="serial_number"
                  control={control}
                  render={({ field }) => (
                    <TextField 
                      {...field} 
                      label="Nº Série" 
                      fullWidth 
                      size="small" 
                      disabled={loading || isSubmitting} 
                      InputLabelProps={{ sx: { fontSize: '0.875rem' } }} 
                      InputProps={{ sx: { height: 40, fontSize: '0.875rem' } }}
                    />
                  )}
                />
              </Grid>

              {/* Linha 2: Data Compra, Data Baixa */}
              <Grid item xs={12} md={3}>
                <Controller
                  name="purchase_date"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Data Compra"
                      type="date"
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true, sx: { fontSize: '0.875rem' } }} 
                      disabled={loading || isSubmitting}
                      value={field.value || ''}
                      InputProps={{ sx: { height: 40, fontSize: '0.875rem' } }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Controller
                  name="discharge_date"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Data Baixa"
                      type="date"
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true, sx: { fontSize: '0.875rem' } }} 
                      disabled={loading || isSubmitting}
                      value={field.value || ''}
                      InputProps={{ sx: { height: 40, fontSize: '0.875rem' } }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="discharge_reason"
                  control={control}
                  render={({ field }) => (
                    <TextField 
                      {...field} 
                      label="Motivo Baixa" 
                      fullWidth 
                      size="small" 
                      disabled={loading || isSubmitting} 
                      InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
                      InputProps={{ sx: { height: 40, fontSize: '0.875rem' } }}
                    />
                  )}
                />
              </Grid>

              {/* Linha 3: Observações */}
              <Grid item xs={12}>
                <Controller
                  name="observation"
                  control={control}
                  render={({ field }) => (
                    <TextField 
                      {...field} 
                      label="Observações"
                      fullWidth 
                      size="small" 
                      multiline 
                      rows={3} 
                      disabled={loading || isSubmitting} 
                      InputLabelProps={{ sx: { fontSize: '0.875rem' } }} 
                      InputProps={{ sx: { fontSize: '0.875rem' } }}
                    />
                  )}
                />
              </Grid>
            </Grid>

            <CardContent sx={{ pt: 1, pb: 2, px: 2 }}>
            <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ mt: 3 }}>
              <Button
                variant="outlined"
                onClick={handleCancel}
                startIcon={<ArrowLeft size={16} />}
                disabled={loading || isSubmitting}
                size="small"
                sx={{ fontSize: '0.875rem' }}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                startIcon={loading || isSubmitting ? <CircularProgress size={16} color="inherit" /> : <Save size={16} />}
                disabled={loading || isSubmitting}
                sx={{ 
                  fontWeight: 'bold',
                  fontSize: '0.875rem',
                  borderRadius: 1,
                  height: 36,
                  px: 2,
                  py: 0.5
                }}
                size="small"
              >
                {loading || isSubmitting ? 'Salvando...' : 'Salvar'}
              </Button>
            </Stack>
            </CardContent>
          </form>
        </CardContent>
      </Card>

      {/* Componentes de Confirmação e Sucesso */}
      <ConfirmationDialog
        open={isConfirmDialogOpen}
        onClose={handleCancelSave}
        onConfirm={handleSubmit(handleConfirmSave)} // Chama o handler do react-hook-form
        title={isEditMode ? "Confirmar Alteração" : "Confirmar Criação"}
        contentText={`Tem certeza que deseja salvar ${isEditMode ? 'as alterações neste' : 'este novo'} equipamento?`}
        isLoading={loading || isSubmitting}
        confirmText={loading || isSubmitting ? 'Salvando...' : 'Confirmar'}
      />

      <SuccessSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        onClose={handleSnackbarClose}
      />
    </Box>
  );
};

export default EquipmentFormPage; 